/**
 * KPI catalog for the Advanced mode "IF · My Business" step.
 *
 * Each KPI is defined once here so the picker, the operator list and
 * the value-input formatting all agree on units and bounds. The engine
 * on the backend reads the same codes to know which fact table to
 * query.
 *
 * Categories are only for grouping in the dropdown; codes are flat.
 */

export const KPI_OPERATORS = [
	{ code: '<',  label: '<',   description: 'strictly below' },
	{ code: '<=', label: '≤',   description: 'below or equal' },
	{ code: '=',  label: '=',   description: 'equal' },
	{ code: '>=', label: '≥',   description: 'above or equal' },
	{ code: '>',  label: '>',   description: 'strictly above' },
]

export const KPI_CATALOG = [
	// --- Fleet & availability ------------------------------------------------
	{ code: 'fleet_available_pct',
	  label: 'Fleet available',
	  category: 'Fleet & availability',
	  unit: '%',
	  min: 0, max: 100, step: 1,
	  hint: 'Percentage of fleet available on the pickup date.' },
	{ code: 'fleet_available_units',
	  label: 'Fleet available units',
	  category: 'Fleet & availability',
	  unit: 'units',
	  min: 0, max: 9999, step: 1,
	  hint: 'Absolute number of vehicles available on the pickup date.' },

	// --- Forecast ------------------------------------------------------------
	{ code: 'forecast_occupancy_pct',
	  label: 'Forecast occupancy',
	  category: 'Forecast',
	  unit: '%',
	  min: 0, max: 200, step: 1,
	  // Behind a feature key: the KPI is specified but nothing produces the
	  // data yet (PRD 06 §2.1), so the server refuses a gate that uses it
	  // unless the operator has `ds_repricing_forecast_kpi`. Offering it in
	  // the picker would be offering a strategy that cannot be saved.
	  feature: 'ds_repricing_forecast_kpi',
	  hint: 'Projected fleet occupancy for the pickup date. Above 100% = expected overbooking.' },
]

/**
 * The KPIs this operator may actually use. An entry with `feature` needs
 * that key in `featuresV2`; everything else is always available.
 */
export function availableKpis(features) {
	return KPI_CATALOG.filter(k => !k.feature || !!features?.[k.feature])
}

export const KPI_BY_CODE = Object.fromEntries(KPI_CATALOG.map(k => [k.code, k]))
export const KPI_CATEGORIES = [...new Set(KPI_CATALOG.map(k => k.category))]

// Human summary for a condition (used by the preview / confirm dialog).
export function describeCondition(cond) {
	if (!cond) return ''
	const kpi = KPI_BY_CODE[cond.kpi]
	if (!kpi) return `${cond.kpi} ${cond.op} ${cond.value}`
	const op = KPI_OPERATORS.find(o => o.code === cond.op)?.label || cond.op
	const val = cond.value == null || cond.value === '' ? '?' : cond.value
	return `${kpi.label} ${op} ${val}${kpi.unit === '%' ? '%' : ''}`
}
